// CSV Import/Export utilities for progress claims data (Column-Based Format)
import { MONTH_NAMES } from "./calculations";

// Legacy row-based interface (for internal transformation)
export interface CSVProgressRow {
  category: string;
  subcategory: string;
  claim_month: string;
  claim_year: string;
  percentage: string;
}

// New column-based headers (dynamic month columns added by template generator)
export const PROGRESS_CSV_BASE_HEADERS = [
  "Category",
  "Subcategory",
  "Contract Value",
];

// Helper to escape CSV values (quote if contains comma)
function escapeCSV(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

// Generate CSV template with column-based format (months as headers)
export function generateProgressCSVTemplate(
  categories: {
    name: string;
    isFixedAmount?: boolean;
    customAmount?: number;
    subcategories: { name: string; amount: number }[];
  }[],
  months: { month: number; year: number }[], // Array of months to include as columns
): string {
  const rows: string[] = [];

  // Generate month headers (e.g., "1/2026", "2/2026")
  const monthHeaders = months.map((m) => `${m.month}/${m.year}`);

  // Header row
  rows.push([...PROGRESS_CSV_BASE_HEADERS, ...monthHeaders].join(","));

  // Add comment row with instructions
  rows.push(
    `# Fill in percentages under each month column. Use formats like: 50%, 25.5, or leave blank/-`,
  );

  for (const cat of categories) {
    if (cat.isFixedAmount && cat.customAmount !== undefined) {
      // Custom/Fixed Amount category - quote the contract value to handle commas
      const contractValue = escapeCSV(
        `$${cat.customAmount.toLocaleString("en-AU")}`,
      );
      const emptyMonths = months.map(() => "0%").join(",");
      rows.push(`${cat.name},FIXED AMOUNT,${contractValue},${emptyMonths}`);
    } else {
      // Regular category with subcategories
      for (const sub of cat.subcategories) {
        const contractValue = escapeCSV(
          `$${sub.amount.toLocaleString("en-AU")}`,
        );
        const emptyMonths = months.map(() => "0%").join(",");
        rows.push(`${cat.name},${sub.name},${contractValue},${emptyMonths}`);
      }
    }
  }

  return rows.join("\n");
}

// Download progress CSV template (column-based)
export function downloadProgressCSVTemplate(
  categories: {
    name: string;
    isFixedAmount?: boolean;
    customAmount?: number;
    subcategories: { name: string; amount: number }[];
  }[],
  months: { month: number; year: number }[],
  projectName: string,
) {
  const csvContent = generateProgressCSVTemplate(categories, months);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);

  // Use first and last month in filename
  const startMonth = months[0];
  const endMonth = months[months.length - 1];
  const filename = `progress_claims_${projectName.replace(
    /[^a-z0-9]/gi,
    "_",
  )}_${startMonth.month}_${startMonth.year}_to_${endMonth.month}_${endMonth.year}.csv`;

  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Parse percentage string - accepts "50%", "50", "50.5%", "-", etc.
export function parsePercentage(value: string): number | null {
  if (!value || value.trim() === "" || value.trim() === "-") return null;

  // Remove % sign and whitespace
  const cleaned = value.trim().replace(/%$/, "").trim();

  // Parse as float
  const parsed = parseFloat(cleaned);
  if (isNaN(parsed)) return null;

  // Allow negative values and >100% for adjustments
  return parsed;
}

// Month name abbreviations for parsing headers like "Sep-24"
const MONTH_ABBREV: { [key: string]: number } = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

// Parse month header in various formats:
// - "1/2026", "12/2025" (M/YYYY)
// - "1/26", "12/25" (M/YY)
// - "Sep-24", "Sep-2024" (Mon-YY or Mon-YYYY)
// - "September-24" (MonthName-YY)
function parseMonthHeader(
  header: string,
): { month: number; year: number } | null {
  const cleaned = header.trim().toLowerCase();

  // Try M/YYYY or M/YY format first
  const slashMatch = cleaned.match(/^(\d{1,2})\/(\d{2,4})$/);
  if (slashMatch) {
    const month = parseInt(slashMatch[1]);
    let year = parseInt(slashMatch[2]);
    // Convert 2-digit year to 4-digit (assume 20xx)
    if (year < 100) year += 2000;
    if (month >= 1 && month <= 12 && year >= 2000 && year <= 2100) {
      return { month, year };
    }
  }

  // Try Mon-YY or Mon-YYYY or MonthName-YY format
  const dashMatch = cleaned.match(/^([a-z]+)-?(\d{2,4})$/);
  if (dashMatch) {
    const monthName = dashMatch[1];
    let year = parseInt(dashMatch[2]);
    // Convert 2-digit year to 4-digit (assume 20xx)
    if (year < 100) year += 2000;
    const month = MONTH_ABBREV[monthName];
    if (month && year >= 2000 && year <= 2100) {
      return { month, year };
    }
  }

  return null;
}

// Parse column-based progress claims CSV
export function parseProgressCSV(csvText: string): CSVProgressRow[] {
  const lines = csvText.trim().split("\n");
  if (lines.length < 2) {
    throw new Error("CSV must have a header row and at least one data row");
  }

  // Find header row (skip comment lines starting with #)
  let headerIndex = 0;
  while (
    headerIndex < lines.length &&
    lines[headerIndex].trim().startsWith("#")
  ) {
    headerIndex++;
  }

  if (headerIndex >= lines.length) {
    throw new Error("No header row found in CSV");
  }

  const headers = parseCSVLine(lines[headerIndex]).map((h) => h.trim());
  const headersLower = headers.map((h) => h.toLowerCase());

  // Detect format: column-based (has month/year headers) vs legacy row-based
  const monthColumns: { index: number; month: number; year: number }[] = [];

  headers.forEach((header, index) => {
    const parsed = parseMonthHeader(header);
    if (parsed) {
      monthColumns.push({ index, month: parsed.month, year: parsed.year });
    }
  });

  // If no month columns detected, try legacy format
  if (monthColumns.length === 0) {
    return parseLegacyProgressCSV(lines, headers, headerIndex);
  }

  // Column-based format detected
  const categoryIndex = headersLower.indexOf("category");
  const subcategoryIndex = headersLower.indexOf("subcategory");

  if (categoryIndex === -1 || subcategoryIndex === -1) {
    throw new Error(
      'Missing required columns: "category" and "subcategory" are required',
    );
  }

  const rows: CSVProgressRow[] = [];

  for (let i = headerIndex + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith("#")) continue;

    const values = parseCSVLine(line);

    const category = values[categoryIndex]?.trim().toUpperCase() || "";
    const subcategory = values[subcategoryIndex]?.trim() || "";

    if (!category || !subcategory) continue;

    // For each month column, create a separate row
    for (const mc of monthColumns) {
      const percentageValue = values[mc.index]?.trim() || "";

      // Skip empty or "-" values
      if (!percentageValue || percentageValue === "-") continue;

      rows.push({
        category,
        subcategory,
        claim_month: mc.month.toString(),
        claim_year: mc.year.toString(),
        percentage: percentageValue,
      });
    }
  }

  return rows;
}

// Legacy row-based parser (for backward compatibility)
function parseLegacyProgressCSV(
  lines: string[],
  headers: string[],
  headerIndex: number,
): CSVProgressRow[] {
  // Validate required headers for legacy format
  const requiredHeaders = ["category", "subcategory", "percentage"];
  for (const required of requiredHeaders) {
    if (!headers.includes(required)) {
      throw new Error(
        `Missing required column: "${required}". CSV must have columns: category, subcategory, percentage (or use column-based format with month/year headers like "1/2026")`,
      );
    }
  }

  const rows: CSVProgressRow[] = [];

  for (let i = headerIndex + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith("#")) continue;

    const values = parseCSVLine(line);

    if (values.length !== headers.length) {
      throw new Error(
        `Row ${i + 1}: Found ${values.length} values but expected ${headers.length} columns. Check for missing commas.`,
      );
    }

    const row: CSVProgressRow = {
      category: "",
      subcategory: "",
      claim_month: "",
      claim_year: "",
      percentage: "",
    };

    headers.forEach((header, index) => {
      const value = values[index]?.trim() || "";
      switch (header) {
        case "category":
          row.category = value.toUpperCase();
          break;
        case "subcategory":
          row.subcategory = value;
          break;
        case "claim_month":
          row.claim_month = value;
          break;
        case "claim_year":
          row.claim_year = value;
          break;
        case "percentage":
          row.percentage = value;
          break;
      }
    });

    // Only add if has category and subcategory
    if (row.category && row.subcategory) {
      rows.push(row);
    }
  }

  return rows;
}

// Parse a single CSV line handling quoted values
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current);

  return result;
}

// Parsed and validated entry ready for display/import
export interface ParsedProgressEntry {
  category: string;
  subcategory: string;
  claim_month: number;
  claim_year: number;
  percentage: number;
  amount: number; // Calculated from percentage * subcategory amount
}

// Validation result with detailed errors
export interface ValidationResult {
  valid: {
    entry: ParsedProgressEntry;
    subcategory_id: string;
    subcategory_amount: number;
  }[];
  errors: {
    row: number;
    category: string;
    subcategory: string;
    message: string;
  }[];
  warnings: string[];
}

// Transform and validate CSV rows against project categories
export function validateProgressCSV(
  rows: CSVProgressRow[],
  categories: {
    name: string;
    isFixedAmount?: boolean;
    customAmount?: number;
    subcategories: { name: string; id: string; amount: number }[];
  }[],
  defaultMonth: number,
  defaultYear: number,
): ValidationResult {
  const valid: ValidationResult["valid"] = [];
  const errors: ValidationResult["errors"] = [];
  const warnings: string[] = [];

  // Build lookup map - includes both subcategories and fixed amount categories
  const subcategoryMap = new Map<string, { id: string; amount: number }>();
  for (const cat of categories) {
    // Check if we have a real "FIXED AMOUNT" subcategory (from DB backfill)
    const fixedSub = cat.subcategories.find(
      (s) => s.name.toUpperCase() === "FIXED AMOUNT",
    );

    if (cat.isFixedAmount && cat.customAmount !== undefined) {
      // Fixed amount category
      const key = `${cat.name.toUpperCase()}|FIXED AMOUNT`;

      if (fixedSub) {
        // Use real subcategory ID if available
        subcategoryMap.set(key, {
          id: fixedSub.id,
          amount: cat.customAmount,
        });
      } else {
        // Fallback for UI-only/missing data (will likely fail DB insert if used)
        subcategoryMap.set(key, {
          id: `FIXED:${cat.name}`,
          amount: cat.customAmount,
        });
      }
    }

    // Always map all subcategories normally too (in case user treats "Fixed Amount" as a normal subcategory)
    for (const sub of cat.subcategories) {
      const key = `${cat.name.toUpperCase()}|${sub.name.toUpperCase()}`;
      // Don't overwrite if we already set it (priority to isFixedAmount logic above)
      if (!subcategoryMap.has(key)) {
        subcategoryMap.set(key, { id: sub.id, amount: sub.amount });
      }
    }
  }

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rowNum = i + 2; // +2 because of header row and 0-indexing

    // Look up subcategory
    const key = `${row.category.toUpperCase()}|${row.subcategory.toUpperCase()}`;
    const subInfo = subcategoryMap.get(key);

    if (!subInfo) {
      // Find similar categories/subcategories for helpful suggestions
      const availableCats = [...new Set(categories.map((c) => c.name))];
      const matchingCat = categories.find(
        (c) => c.name.toUpperCase() === row.category.toUpperCase(),
      );

      if (!matchingCat) {
        errors.push({
          row: rowNum,
          category: row.category,
          subcategory: row.subcategory,
          message: `Category "${row.category}" not found. Available categories: ${availableCats.join(", ")}`,
        });
      } else {
        const availableSubs = matchingCat.subcategories.map((s) => s.name);
        errors.push({
          row: rowNum,
          category: row.category,
          subcategory: row.subcategory,
          message: `Subcategory "${row.subcategory}" not found in "${row.category}". Available: ${availableSubs.join(", ")}`,
        });
      }
      continue;
    }

    // Parse percentage
    const percentage = parsePercentage(row.percentage);
    if (percentage === null) {
      errors.push({
        row: rowNum,
        category: row.category,
        subcategory: row.subcategory,
        message: `Invalid percentage "${row.percentage}". Use format like "50%" or "25.5" or "75%"`,
      });
      continue;
    }

    // Skip 0% entries (no change)
    if (percentage === 0) {
      continue;
    }

    // Calculate amount from percentage
    const amount = (subInfo.amount * percentage) / 100;

    // Parse month/year with defaults
    let claim_month: number = defaultMonth;
    if (row.claim_month) {
      // Try parsing as number first
      const parsedMonth = parseInt(row.claim_month);
      if (!isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12) {
        claim_month = parsedMonth;
      } else {
        // Try parsing as name (e.g. "Jan", "January")
        const lowerInput = row.claim_month.toLowerCase();
        const monthIndex = MONTH_NAMES.findIndex((m) =>
          m.toLowerCase().startsWith(lowerInput),
        );
        if (monthIndex >= 0) {
          claim_month = monthIndex + 1;
        } else {
          errors.push({
            row: rowNum,
            category: row.category,
            subcategory: row.subcategory,
            message: `Invalid month "${row.claim_month}". Use number (1-12) or name (e.g. Jan, January)`,
          });
          continue;
        }
      }
    }

    const claim_year = row.claim_year ? parseInt(row.claim_year) : defaultYear;
    if (isNaN(claim_year) || claim_year < 2000 || claim_year > 2100) {
      errors.push({
        row: rowNum,
        category: row.category,
        subcategory: row.subcategory,
        message: `Invalid year "${row.claim_year}".`,
      });
      continue;
    }

    valid.push({
      entry: {
        category: row.category,
        subcategory: row.subcategory,
        claim_month,
        claim_year,
        percentage,
        amount,
      },
      subcategory_id: subInfo.id,
      subcategory_amount: subInfo.amount,
    });
  }

  // Add warning if no valid entries
  if (valid.length === 0 && errors.length === 0) {
    warnings.push(
      "No entries with percentage > 0% found. Fill in the percentage columns with values like 50% or 25.",
    );
  }

  return { valid, errors, warnings };
}

// Format validation result for display
export function formatValidationSummary(result: ValidationResult): {
  title: string;
  stats: { label: string; value: string | number; color?: string }[];
  hasErrors: boolean;
} {
  const totalAmount = result.valid.reduce((sum, v) => sum + v.entry.amount, 0);

  return {
    title:
      result.errors.length > 0
        ? `Found ${result.valid.length} valid entries and ${result.errors.length} errors`
        : `Ready to import ${result.valid.length} progress entries`,
    stats: [
      {
        label: "Valid Entries",
        value: result.valid.length,
        color: "var(--color-success)",
      },
      {
        label: "Errors",
        value: result.errors.length,
        color: result.errors.length > 0 ? "var(--color-error)" : undefined,
      },
      {
        label: "Total Claim Amount",
        value: `$${totalAmount.toLocaleString("en-AU", {
          minimumFractionDigits: 2,
        })}`,
      },
    ],
    hasErrors: result.errors.length > 0 && result.valid.length === 0,
  };
}
